import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { 
  Shield, 
  Clock,
  Activity,
  Users,
  AlertTriangle,
  CheckCircle,
  XCircle,
  Eye,
  Globe,
  Smartphone
} from 'lucide-react';
import { userDetailService, UserLoginHistory } from '@/services/admin/user-detail-service';
import { UserData } from '@/services/admin/user-management-service';
import { formatDistanceToNow, format } from 'date-fns';
import { toast } from 'sonner';

interface UserSecurityAuditTabProps {
  userId: string;
  user: UserData;
}

export const UserSecurityAuditTab: React.FC<UserSecurityAuditTabProps> = ({ userId, user }) => {
  const [loginHistory, setLoginHistory] = useState<UserLoginHistory[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const fetchData = async () => {
    try {
      const historyData = await userDetailService.getUserLoginHistory(userId);
      setLoginHistory(historyData);
    } catch (error) {
      console.error('Error fetching security data:', error);
      toast.error('Failed to load security data');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [userId]);

  const getSecurityScore = () => {
    let score = 0;
    const factors = [];

    // Account age (older = more trustworthy)
    const accountAge = Math.floor((Date.now() - new Date(user.registrationDate).getTime()) / (1000 * 60 * 60 * 24));
    if (accountAge > 90) {
      score += 25;
      factors.push('Established account (90+ days)');
    } else if (accountAge > 30) {
      score += 15;
      factors.push('Account age 30+ days');
    }

    // Recent activity
    if (user.lastLogin && new Date(user.lastLogin) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)) {
      score += 20;
      factors.push('Recent activity');
    }

    // Admin status
    if (user.isAdmin) {
      score += 15;
      factors.push('Admin privileges');
    }

    // Subscription status
    if (user.subscriptionStatus === 'active' && user.subscriptionPlan !== 'free') {
      score += 20;
      factors.push('Active paid subscription');
    }

    // Affiliate referral
    if (user.source === 'affiliate') {
      score += 10;
      factors.push('Referred by affiliate');
    }

    // Usage patterns
    if (user.aiMessagesUsed > 50 || user.marketAnalysisUsage > 10) {
      score += 10;
      factors.push('Regular platform usage');
    }

    return { score: Math.min(score, 100), factors };
  };

  const securityInfo = getSecurityScore();

  const getSecurityBadge = (score: number) => {
    if (score >= 80) {
      return (
        <Badge className="bg-green-100 text-green-800 border-green-200">
          <CheckCircle className="h-3 w-3 mr-1" />
          High Trust
        </Badge>
      );
    }
    if (score >= 60) {
      return (
        <Badge className="bg-yellow-100 text-yellow-800 border-yellow-200">
          <AlertTriangle className="h-3 w-3 mr-1" />
          Medium Trust
        </Badge>
      );
    }
    return (
      <Badge className="bg-red-100 text-red-800 border-red-200">
        <XCircle className="h-3 w-3 mr-1" />
        Low Trust
      </Badge>
    );
  };

  const getDeviceIcon = (userAgent?: string) => {
    if (!userAgent) return <Globe className="h-4 w-4" />;
    
    if (userAgent.toLowerCase().includes('mobile') || userAgent.toLowerCase().includes('android') || userAgent.toLowerCase().includes('iphone')) {
      return <Smartphone className="h-4 w-4" />;
    }
    return <Globe className="h-4 w-4" />;
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        {Array.from({ length: 3 }).map((_, i) => (
          <Card key={i}>
            <CardHeader>
              <Skeleton className="h-6 w-48" />
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-4">
                  <Skeleton className="h-16" />
                  <Skeleton className="h-16" />
                </div>
                <Skeleton className="h-24 w-full" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Security Overview */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Security Score</p>
                <p className="text-2xl font-bold">{securityInfo.score}/100</p>
              </div>
              <Shield className="h-8 w-8 text-blue-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Account Age</p>
                <p className="text-lg font-bold">
                  {Math.floor((Date.now() - new Date(user.registrationDate).getTime()) / (1000 * 60 * 60 * 24))} days
                </p>
              </div>
              <Clock className="h-8 w-8 text-green-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Login Sessions</p>
                <p className="text-2xl font-bold">{loginHistory.length}</p>
              </div>
              <Activity className="h-8 w-8 text-purple-500" />
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-muted-foreground">Trust Level</p>
                <div className="mt-1">
                  {getSecurityBadge(securityInfo.score)}
                </div>
              </div>
              <Users className="h-8 w-8 text-orange-500" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Security Assessment */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Shield className="h-5 w-5" />
            Security Assessment
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div>
                <h3 className="font-medium mb-2">Trust Score: {securityInfo.score}/100</h3>
                <div className="w-full bg-gray-200 rounded-full h-3">
                  <div 
                    className={`h-3 rounded-full ${
                      securityInfo.score >= 80 ? 'bg-green-500' : 
                      securityInfo.score >= 60 ? 'bg-yellow-500' : 'bg-red-500'
                    }`}
                    style={{ width: `${securityInfo.score}%` }}
                  />
                </div>
              </div>
              
              <div>
                <h4 className="font-medium text-sm mb-2">Contributing Factors:</h4>
                <ul className="space-y-1">
                  {securityInfo.factors.map((factor, index) => (
                    <li key={index} className="text-sm text-muted-foreground flex items-center gap-2">
                      <CheckCircle className="h-3 w-3 text-green-500" />
                      {factor}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
            
            <div className="space-y-4">
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Account Status</span>
                  <Badge variant={user.isActive ? 'default' : 'secondary'}>
                    {user.isActive ? 'Active' : 'Inactive'}
                  </Badge>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Admin Privileges</span>
                  <Badge variant={user.isAdmin ? 'destructive' : 'outline'}>
                    {user.isAdmin ? 'Yes' : 'No'}
                  </Badge>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Registration Source</span>
                  <Badge variant="outline">
                    {user.source === 'affiliate' ? `Affiliate: ${user.affiliateName}` : 'Organic'}
                  </Badge>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Last Login</span>
                  <span className="text-sm">
                    {user.lastLogin ? formatDistanceToNow(new Date(user.lastLogin), { addSuffix: true }) : 'Never'}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Login History */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Activity className="h-5 w-5" />
            Recent Login History
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loginHistory.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-12 w-12 mx-auto mb-4 text-muted-foreground/50" />
              <p>No login history available</p>
            </div>
          ) : (
            <div className="space-y-4">
              {loginHistory.slice(0, 10).map((session) => (
                <div key={session.sessionId} className="flex items-center justify-between p-3 border rounded-lg">
                  <div className="flex items-center gap-3">
                    {getDeviceIcon(session.userAgent)}
                    <div>
                      <div className="font-medium">Session {session.sessionId.slice(0, 8)}</div>
                      <div className="text-sm text-muted-foreground">
                        Login: {format(new Date(session.createdAt), 'PPp')}
                      </div>
                      {session.ipAddress && (
                        <div className="text-xs text-muted-foreground">
                          IP: {session.ipAddress}
                        </div>
                      )}
                    </div>
                  </div>
                  
                  <div className="text-right">
                    <div className="text-sm font-medium">
                      {formatDistanceToNow(new Date(session.lastActivity), { addSuffix: true })}
                    </div>
                    <div className="text-xs text-muted-foreground">
                      Last activity
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Referral Information */}
      {user.source === 'affiliate' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" />
              Referral Information
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-4">
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Referred By</span>
                  <Badge variant="outline" className="bg-green-50 text-green-700 border-green-200">
                    {user.affiliateName}
                  </Badge>
                </div>
                
                <div className="flex justify-between">
                  <span className="text-sm font-medium text-muted-foreground">Referral Date</span>
                  <span className="text-sm">{format(new Date(user.registrationDate), 'PPP')}</span>
                </div>
              </div>
              
              <div className="flex items-center justify-center">
                <Button variant="outline" size="sm">
                  <Eye className="h-4 w-4 mr-2" />
                  View Affiliate Details
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Security Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Security Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Button variant="outline" className="justify-start">
              <Shield className="h-4 w-4 mr-2" />
              Reset Password
            </Button>
            
            <Button variant="outline" className="justify-start">
              <Activity className="h-4 w-4 mr-2" />
              View Full Activity Log
            </Button>
            
            <Button variant="destructive" className="justify-start">
              <XCircle className="h-4 w-4 mr-2" />
              Suspend Account
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};